import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaTshirt, FaUsers, FaStickyNote, FaExternalLinkAlt, FaPlus, FaEdit, FaTag } from "react-icons/fa";
import "./AvailabilityModal.css";
import { formatPracticeTimeRange } from "./utils";

function normalizeCoachStatus(value) {
  const status = typeof value === "string" ? value.toLowerCase() : "";
  if (status === "present" || status === "available") return "available";
  if (status === "absent" || status === "not available" || status === "unavailable") return "not available";
  return null;
}

function formatPracticeTypeLabel(typeValue) {
  const value = String(typeValue || "").trim().toLowerCase();
  if (value === "event") return "Event";
  if (value === "regatta") return "Regatta";
  return "Practice";
}
function normalizePracticeType(typeValue) {
  const value = String(typeValue || "").trim().toLowerCase();
  if (["event", "practice", "regatta"].includes(value)) return value;
  return "practice";
}

function navigateCoachTab(tab, payload = null) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("coach:navigate", { detail: { tab, payload } }));
  } catch (err) {
    console.warn("Failed to dispatch coach navigation event:", err);
  }
}

export default function PracticeModal({
  isOpen,
  onClose,
  practice,
  allowEdit = false,
  form = {},
  onChangeForm = () => {},
  teams = [],
  squads = [],
  onSave,
  onDelete,
  onAddNote,
  onCancelPractice,
  loadingDetails = false,
  practiceDetails = null,
  coach,
  onUpdateOwnAttendance,
  updatingCoachAttendance = false,
  showRsvpButtons = false,
  rsvpStatus,
  rsvpResponseData = null,
  enableResponseFields = true,
  onRsvp,
  modalTitle,
  startInEditMode = false,
  hideEditToggle = false,
  canEditWorkouts = false,
  hideExtendedDetails = false,
}) {
  const [coachesExpanded, setCoachesExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [savingRsvp, setSavingRsvp] = useState(false);
  const [optimisticRsvpStatus, setOptimisticRsvpStatus] = useState(null); // Track optimistic RSVP
  const [responseInputs, setResponseInputs] = useState({});
  const [showSameDayAlert, setShowSameDayAlert] = useState(false);
  const pendingRsvpRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCoachesExpanded(false);
      setIsEditing(startInEditMode);
      setOptimisticRsvpStatus(null); // Reset optimistic status when modal opens
    }
  }, [isOpen, practice?.id, startInEditMode]);

  useEffect(() => {
    if (!isOpen) return;
    const base =
      rsvpResponseData && typeof rsvpResponseData === "object" && !Array.isArray(rsvpResponseData)
        ? rsvpResponseData
        : {};
    setResponseInputs({ ...base });
  }, [isOpen, practice?.id, rsvpResponseData]);

  // Reset optimistic status when the real rsvpStatus prop updates from the server
  const prevRsvpStatusRef = useRef(rsvpStatus);
  useEffect(() => {
    if (prevRsvpStatusRef.current !== rsvpStatus) {
      prevRsvpStatusRef.current = rsvpStatus;
      setOptimisticRsvpStatus(null);
    }
  }, [rsvpStatus]);

  if (!isOpen || !practice) return null;

  const resolvedPractice = { ...practice, ...(practiceDetails?.practiceInfo || {}) };
  const displayTitle = modalTitle || resolvedPractice.name || "Practice";
  const practiceId = resolvedPractice?.id;
  const workouts = Array.isArray(practiceDetails?.workouts) ? practiceDetails.workouts : [];
  const lineupStatuses = Array.isArray(practiceDetails?.lineupStatuses) ? practiceDetails.lineupStatuses : [];
  const responseFields = Array.isArray(resolvedPractice?.responseFields) ? resolvedPractice.responseFields : [];
  const isCoachViewer = Boolean(coach?.id);
  const responseFieldsEnabled = enableResponseFields && responseFields.length > 0 && showRsvpButtons && onRsvp;
  const responseFieldStatus = optimisticRsvpStatus !== null ? optimisticRsvpStatus : rsvpStatus;
  const responseFieldStatusNormalized = typeof responseFieldStatus === "string" ? responseFieldStatus.toLowerCase() : "";
  const responseFieldsVisible =
    responseFieldsEnabled &&
    (responseFieldStatusNormalized === "available" || Object.keys(responseInputs || {}).length > 0);
  const canViewLineups = Boolean(
    coach &&
      (coach.permissions?.includes?.("lineups:view") ||
        coach.permissions?.includes?.("lineups:edit") ||
        coach.can_view_lineup ||
        coach.can_edit_lineup)
  );
  const canCreateWorkouts = Boolean(
    canEditWorkouts ||
      coach?.permissions?.includes?.("workouts:edit") ||
      coach?.permissions?.includes?.("practices:edit") ||
      coach?.can_edit_workouts ||
      coach?.can_edit_practices
  );

  function populateFormFromPractice() {
    if (!allowEdit || !onChangeForm) return;
    onChangeForm({
      name: resolvedPractice.name || "",
      abbrevName: resolvedPractice.abbrevName || "",
      location: resolvedPractice.location || "",
      date: resolvedPractice.startDate || resolvedPractice.date || "",
      endDate: resolvedPractice.endDate || "",
      startTime: (resolvedPractice.startTime || resolvedPractice.time || "").slice(0, 5),
      endTime: (resolvedPractice.endTime || "").slice(0, 5),
      team: resolvedPractice.team || "",
      squadIds: Array.isArray(resolvedPractice.squadIds) ? resolvedPractice.squadIds.map(String) : (resolvedPractice.squadId ? [String(resolvedPractice.squadId)] : []),
      note: resolvedPractice.note || "",
      type: normalizePracticeType(resolvedPractice.type),
      responseFields: Array.isArray(resolvedPractice.responseFields)
        ? resolvedPractice.responseFields.map((field) => ({
            id: field.id,
            label: field.label || "",
            type: field.type || "text",
            options: Array.isArray(field.options) ? field.options : [],
            optionsText: Array.isArray(field.options) ? field.options.join("\n") : "",
          }))
        : [],
    });
  }

  function renderEditForm() {
    if (!allowEdit) return null;
    const isRegatta = normalizePracticeType(form.type) === "regatta";
    const responseFieldsForm = Array.isArray(form.responseFields) ? form.responseFields : [];
    const updateResponseFields = (next) => onChangeForm({ ...form, responseFields: next });
    const addResponseField = () => {
      const id = `field_${Date.now()}`;
      updateResponseFields([
        ...responseFieldsForm,
        { id, label: "", type: "text", options: [], optionsText: "" },
      ]);
    };
    return (
      <div className="practiceEditForm">
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <label className="stack" style={{ flex: "1 1 200px", gap: 4 }}>
            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Name</span>
            <input
              className="input"
              type="text"
              value={form.name || ""}
              onChange={(e) => onChangeForm({ ...form, name: e.target.value })}
              placeholder="Morning Practice"
            />
          </label>
          <label className="stack" style={{ flex: "1 1 200px", gap: 4 }}>
            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Location</span>
            <input
              className="input"
              type="text"
              value={form.location || ""}
              onChange={(e) => onChangeForm({ ...form, location: e.target.value })}
              placeholder="Boathouse"
            />
          </label>
          <label className="stack" style={{ flex: "1 1 160px", gap: 4 }}>
            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Date</span>
            <input
              className="input"
              type="date"
              value={form.date || ""}
              onChange={(e) => onChangeForm({ ...form, date: e.target.value })}
            />
          </label>
          <label className="stack" style={{ flex: "1 1 160px", gap: 4 }}>
            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>End date (optional)</span>
            <input
              className="input"
              type="date"
              value={form.endDate || ""}
              onChange={(e) => onChangeForm({ ...form, endDate: e.target.value })}
            />
          </label>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <label className="stack" style={{ flex: "1 1 120px", gap: 4 }}>
            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Start time</span>
            <input
              className="input"
              type="time"
              value={form.startTime || ""}
              onChange={(e) => onChangeForm({ ...form, startTime: e.target.value })}
            />
          </label>
          <label className="stack" style={{ flex: "1 1 120px", gap: 4 }}>
            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>End time</span>
            <input
              className="input"
              type="time"
              value={form.endTime || ""}
              onChange={(e) => onChangeForm({ ...form, endTime: e.target.value })}
            />
          </label>
          <label className="stack" style={{ flex: "1 1 140px", gap: 4 }}>
            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Type</span>
            <select
              className="input"
              value={form.type || "practice"}
              onChange={(e) => {
                const nextType = e.target.value;
                onChangeForm({
                  ...form,
                  type: nextType,
                  ...(normalizePracticeType(nextType) === "regatta" ? {} : { abbrevName: "" }),
                });
              }}
            >
              <option value="practice">Practice</option>
              <option value="event">Event</option>
              <option value="regatta">Regatta</option>
            </select>
          </label>
          {isRegatta && (
            <label className="stack" style={{ flex: "1 1 160px", gap: 4 }}>
              <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>
                Regatta short name
              </span>
              <input
                className="input"
                type="text"
                value={form.abbrevName || ""}
                maxLength={10}
                onChange={(e) => onChangeForm({ ...form, abbrevName: e.target.value.slice(0, 10) })}
                placeholder="Max 10 chars"
              />
            </label>
          )}
          <label className="stack" style={{ flex: "1 1 140px", gap: 4 }}>
            <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Team</span>
            <select
              className="input"
              value={form.team || ""}
              onChange={(e) => onChangeForm({ ...form, team: e.target.value })}
            >
              <option value="">All teams</option>
              {(teams || []).map((team) => (
                <option key={team.id} value={team.name}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>

          {(squads || []).length > 0 && (
            <div className="stack" style={{ flex: "1 1 140px", gap: 4 }}>
              <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Squad</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--bg-elevated)" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", color: "var(--text)" }}>
                  <input
                    type="checkbox"
                    checked={(form.squadIds || []).length === 0}
                    onChange={() => onChangeForm({ ...form, squadIds: [] })}
                  />
                  All squads
                </label>
                {(squads || []).map((sq) => {
                  const checked = (form.squadIds || []).map(String).includes(String(sq.id));
                  return (
                    <label key={sq.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const current = (form.squadIds || []).map(String);
                          const next = checked
                            ? current.filter((id) => id !== String(sq.id))
                            : [...current, String(sq.id)];
                          onChangeForm({ ...form, squadIds: next });
                        }}
                      />
                      {sq.name}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="practiceSubsection" style={{ marginTop: 4 }}>
          <div className="practiceSectionLabel">
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}><FaTag />Response fields</span>
            <button type="button" className="btn ghost tiny" onClick={addResponseField}>
              + Add field
            </button>
          </div>
          {responseFieldsForm.length === 0 ? (
            <p className="help" style={{ margin: 0, textTransform: "none", letterSpacing: 0, fontWeight: 400, fontSize: 13 }}>No response fields configured.</p>
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              {responseFieldsForm.map((field, idx) => (
                <div key={field.id || idx} className="stack" style={{ gap: 6, border: "1px solid var(--card-border)", borderRadius: 11, padding: 10, background: "var(--bg-elevated)" }}>
                  <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                    <label className="stack" style={{ flex: "1 1 200px", gap: 4 }}>
                      <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Label</span>
                      <input
                        className="input"
                        type="text"
                        value={field.label || ""}
                        onChange={(e) => {
                          const next = responseFieldsForm.map((item, i) =>
                            i === idx ? { ...item, label: e.target.value } : item
                          );
                          updateResponseFields(next);
                        }}
                        placeholder="Transportation"
                      />
                    </label>
                    <label className="stack" style={{ flex: "0 0 150px", gap: 4 }}>
                      <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Type</span>
                      <select
                        className="input"
                        value={field.type || "text"}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          const next = responseFieldsForm.map((item, i) =>
                            i === idx
                              ? {
                                  ...item,
                                  type: nextType,
                                  options: nextType === "picklist" ? (item.options || []) : [],
                                  optionsText:
                                    nextType === "picklist"
                                      ? (item.optionsText ?? (item.options || []).join("\n"))
                                      : "",
                                }
                              : item
                          );
                          updateResponseFields(next);
                        }}
                      >
                        <option value="text">Text</option>
                        <option value="picklist">Picklist</option>
                      </select>
                    </label>
                    <div style={{ display: "flex", alignItems: "flex-end" }}>
                      <button
                        type="button"
                        className="btn ghost tiny"
                        onClick={() => updateResponseFields(responseFieldsForm.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  {String(field.type || "text").toLowerCase() === "picklist" && (
                    <label className="stack" style={{ gap: 4 }}>
                      <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Options (one per line)</span>
                      <textarea
                        className="input"
                        style={{ minHeight: "72px", resize: "vertical", fontFamily: "inherit" }}
                        value={
                          typeof field.optionsText === "string"
                            ? field.optionsText
                            : (field.options || []).join("\n")
                        }
                        onChange={(e) => {
                          const optionsText = e.target.value;
                          const options = optionsText
                            .split("\n")
                            .map((opt) => opt.trim())
                            .filter((opt) => opt);
                          const next = responseFieldsForm.map((item, i) =>
                            i === idx ? { ...item, optionsText, options } : item
                          );
                          updateResponseFields(next);
                        }}
                        placeholder="Carpool\nDrive\nWalk"
                      />
                      <span className="help" style={{ margin: 0 }}>Commas and spaces are allowed inside an option.</span>
                    </label>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <label className="stack" style={{ width: "100%", gap: 4 }}>
          <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>Note</span>
          <textarea
            className="input"
            style={{ minHeight: "60px", resize: "vertical", fontFamily: "inherit" }}
            placeholder="Optional note"
            value={form.note || ""}
            onChange={(e) => onChangeForm({ ...form, note: e.target.value })}
          />
        </label>
      </div>
    );
  }

  function renderSummary() {
    const startDateValue = resolvedPractice.startDate || resolvedPractice.date;
    const endDateValue = resolvedPractice.endDate || null;
    const hasRange = endDateValue && startDateValue && endDateValue > startDateValue;
    const dateText = startDateValue
      ? new Date(`${startDateValue}T00:00:00`).toLocaleDateString(undefined, {
          weekday: "long",
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "Date pending";
    const endDateText = hasRange
      ? new Date(`${endDateValue}T00:00:00`).toLocaleDateString(undefined, {
          weekday: "long",
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
    const timeLabel = formatPracticeTimeRange(resolvedPractice);
    const squadIds = Array.isArray(resolvedPractice.squadIds) ? resolvedPractice.squadIds : [];
    const resolvedSquadNames = squadIds.length > 0
      ? squadIds.map((sid) => {
          const sq = (squads || []).find((s) => String(s.id) === String(sid));
          return sq ? sq.name : null;
        }).filter(Boolean)
      : [];
    const squadLabel = resolvedSquadNames.length > 0
      ? resolvedSquadNames.join(", ")
      : (resolvedPractice.squadName || resolvedPractice.squad || "All squads");
    const locationLabel = resolvedPractice.location || "";
    const typeLabel = formatPracticeTypeLabel(resolvedPractice.type);
    const infoRows = [
      { icon: FaMapMarkerAlt, label: "Location", value: locationLabel || "Location not set" },
      { icon: FaTag, label: "Type", value: typeLabel },
      resolvedPractice.team
        ? { icon: FaTshirt, label: "Team", value: resolvedPractice.team }
        : { icon: FaTshirt, label: "Team", value: "All teams" },
      { icon: FaUsers, label: "Squad", value: squadLabel },
    ];
    return (
      <div className="practiceSummarySleek">
        {resolvedPractice.cancelled && (
          <div
            className="practiceCancelledBanner"
            style={{
              background: "var(--danger-bg, #fef2f2)",
              border: "1px solid var(--danger-border, #fecaca)",
              borderRadius: 6,
              padding: "10px 12px",
              marginBottom: 12,
              color: "var(--danger, #b91c1c)",
              fontSize: 14,
            }}
          >
            <strong>Cancelled</strong>
            {resolvedPractice.cancellationNote ? `: ${resolvedPractice.cancellationNote}` : ""}
          </div>
        )}
        <div className="practiceSummaryDateRow">
          <FaCalendarAlt />
          <span>{hasRange ? `${dateText} – ${endDateText}` : dateText}</span>
          {timeLabel && (
            <>
              <span className="practiceSummaryDateSep">•</span>
              <FaClock />
              <span>{timeLabel}</span>
            </>
          )}
        </div>
        <div className="practiceSummaryGrid">
          {infoRows.map((row, idx) => {
            const IconComponent = row.icon;
            return (
              <div key={idx} className="practiceSummaryCard">
                <span className="practiceSummaryCardIcon"><IconComponent /></span>
                <div>
                  <div className="practiceSummaryCardLabel">{row.label}</div>
                  <div className="practiceSummaryCardValue">{row.value}</div>
                </div>
              </div>
            );
          })}
        </div>
        {resolvedPractice.note ? (
          <div className="practiceSummaryNote">
            <FaStickyNote /> <span>{resolvedPractice.note}</span>
          </div>
        ) : null}
      </div>
    );
  }

  function renderCoachSummary() {
    const coaches = Array.isArray(practiceDetails?.coaches) ? practiceDetails.coaches : [];
    if (coaches.length === 0) {
      return <p className="help" style={{ margin: 0 }}>No coaches found</p>;
    }

    if (!coachesExpanded) {
      const { availableCount, unavailableCount, unknownCount } = coaches.reduce(
        (acc, c) => {
          const normalized = normalizeCoachStatus(c.status);
          if (normalized === "available") acc.availableCount += 1;
          else if (normalized === "not available") acc.unavailableCount += 1;
          else acc.unknownCount += 1;
          return acc;
        },
        { availableCount: 0, unavailableCount: 0, unknownCount: 0 }
      );
      return (
        <div className="practiceCoachTally">
          {availableCount === 0 && unavailableCount === 0 && unknownCount === 0 ? (
            <span>No responses yet</span>
          ) : (
            <span>
              {availableCount > 0 && <span style={{ color: "var(--success)" }}>{availableCount} Available</span>}
              {availableCount > 0 && (unavailableCount > 0 || unknownCount > 0) && " • "}
              {unavailableCount > 0 && <span style={{ color: "var(--danger)" }}>{unavailableCount} Not Available</span>}
              {unavailableCount > 0 && unknownCount > 0 && " • "}
              {unknownCount > 0 && <span style={{ color: "var(--muted)" }}>{unknownCount} Unknown</span>}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className="practiceCoachList">
        {[...coaches]
          .sort((a, b) => {
            const aIsCurrent = coach?.id && a.userId === coach.id;
            const bIsCurrent = coach?.id && b.userId === coach.id;
            if (aIsCurrent && !bIsCurrent) return -1;
            if (!aIsCurrent && bIsCurrent) return 1;
            return 0;
          })
          .map((coachData, idx) => {
            const isCurrentCoach = coach?.id && coachData.userId === coach.id;
            const normalizedStatus = normalizeCoachStatus(coachData.status);
            const statusColor =
              normalizedStatus === "available"
                ? "var(--success)"
                : normalizedStatus === "not available"
                ? "var(--danger)"
                : "var(--muted)";
            const statusLabel =
              normalizedStatus === "available"
                ? "Available"
                : normalizedStatus === "not available"
                ? "Not Available"
                : "Unknown";
            return (
              <div
                key={idx}
                className={`practiceCoachRow${isCurrentCoach ? " practiceCoachRowMe" : ""}`}
              >
                <span style={{ fontWeight: isCurrentCoach ? 600 : 400 }}>
                  {coachData.name}
                  {isCurrentCoach ? " (You)" : ""}
                </span>
                {isCurrentCoach && onUpdateOwnAttendance ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      className="btn tiny"
                      style={{
                        background: normalizedStatus === "available" ? "#16a34a" : "var(--success-bg)",
                        color: normalizedStatus === "available" ? "#fff" : "var(--success)",
                        borderColor: "var(--success-border)",
                        padding: "4px 8px",
                        fontSize: 11,
                      }}
                      onClick={() => onUpdateOwnAttendance(practiceId, "available")}
                      disabled={updatingCoachAttendance || normalizedStatus === "available" || !practiceId}
                    >
                      Available
                    </button>
                    <button
                      type="button"
                      className="btn tiny"
                      style={{
                        background: normalizedStatus === "not available" ? "#dc2626" : "var(--danger-bg)",
                        color: normalizedStatus === "not available" ? "#fff" : "var(--danger)",
                        borderColor: "var(--danger-border)",
                        padding: "4px 8px",
                        fontSize: 11,
                      }}
                      onClick={() => onUpdateOwnAttendance(practiceId, "not available")}
                      disabled={updatingCoachAttendance || normalizedStatus === "not available" || !practiceId}
                    >
                      Not Available
                    </button>
                  </div>
                ) : (
                  <span style={{ color: statusColor, fontWeight: 500, fontSize: 12 }}>
                    {statusLabel}
                  </span>
                )}
              </div>
            );
          })}
      </div>
    );
  }

  function renderAttendanceSummary() {
    if (!practiceDetails?.attendance) {
      return <p className="help" style={{ margin: 0 }}>No attendance data</p>;
    }

    if (Array.isArray(practiceDetails.attendance)) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {practiceDetails.attendance.map((teamSummary, idx) => (
            <div key={idx} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
                {teamSummary.team || teamSummary.teamName || "Unassigned"}
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 12, paddingLeft: 8 }}>
                <span style={{ color: "var(--success)" }}>
                  Available: {teamSummary.available || 0}
                </span>
                <span style={{ color: "var(--danger)" }}>
                  Not Available: {teamSummary.unavailable || 0}
                </span>
                <span style={{ color: "var(--muted)" }}>
                  Unknown: {teamSummary.unknown || 0}
                </span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (practiceDetails.attendance.overall) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
          <div className="practiceAttendancePill" style={{ background: "var(--success-bg)" }}>
            <span>Available:</span>
            <strong style={{ color: "var(--success)" }}>{practiceDetails.attendance.overall.available || 0}</strong>
          </div>
          <div className="practiceAttendancePill" style={{ background: "var(--danger-bg)" }}>
            <span>Not Available:</span>
            <strong style={{ color: "var(--danger)" }}>{practiceDetails.attendance.overall.unavailable || 0}</strong>
          </div>
          <div className="practiceAttendancePill" style={{ background: "var(--bg-elevated)" }}>
            <span>Unknown:</span>
            <strong style={{ color: "var(--muted)" }}>{practiceDetails.attendance.overall.unknown || 0}</strong>
          </div>
        </div>
      );
    }

    return <p className="help" style={{ margin: 0 }}>No attendance data</p>;
  }

  function renderDetailsSection() {
    if (loadingDetails) {
      return (
        <div style={{ marginTop: 12 }}>
          <p className="help">Loading practice details...</p>
        </div>
      );
    }

    if (!practiceDetails) return null;

    // If hideExtendedDetails is true, only show RSVP buttons
    if (hideExtendedDetails) {
      return (
        <div style={{ marginTop: 16 }}>
          {showRsvpButtons && onRsvp ? (
            <div style={{ width: "100%", marginBottom: 8 }}>
              {renderRsvpButtons()}
            </div>
          ) : null}
          {responseFieldsVisible ? (
            <div style={{ width: "100%", marginBottom: 12 }}>
              {renderResponseFields()}
            </div>
          ) : null}
        </div>
      );
    }

    const workoutSection = renderWorkoutSection();
    const lineupSection = renderLineupSection();
    return (
      <div className="practiceDetailsSleek">
        {showRsvpButtons && onRsvp ? (
          <div style={{ width: "100%", marginBottom: 8 }}>
            {renderRsvpButtons()}
          </div>
        ) : null}
        {responseFieldsVisible ? (
          <div style={{ flex: "1 1 100%" }}>
            {renderResponseFields()}
          </div>
        ) : null}
        <div className="practiceSubsection">
          <div
            className="practiceSectionLabel practiceSectionLabelClickable"
            onClick={() => setCoachesExpanded(!coachesExpanded)}
          >
            <span>Available Coaches</span>
            <span className="practiceSectionChevron">
              {coachesExpanded ? "▼" : "▶"}
            </span>
          </div>
          {renderCoachSummary()}
        </div>

        <div className="practiceSubsection">
          <div className="practiceSectionLabel">
            <span>Athlete Attendance</span>
            {practiceId && isCoachViewer && (
              <button
                type="button"
                className="btn ghost"
                style={{ padding: "4px 8px", minWidth: "auto" }}
                onClick={() => navigateCoachTab("attendance", { practiceId, date: resolvedPractice.date })}
                aria-label="Open attendance"
              >
                <FaExternalLinkAlt />
              </button>
            )}
          </div>
          {renderAttendanceSummary()}
        </div>
        {workoutSection}
        {lineupSection}
        {renderNotesSection()}
      </div>
    );
  }

  function renderNotesSection() {
    if (!practiceId || !isCoachViewer) return null;
    return (
      <div className="practiceSubsection">
        <div className="practiceSectionLabel">
          <span>Notes</span>
          {onAddNote && (
            <button
              type="button"
              className="btn ghost"
              style={{ padding: "4px 8px", minWidth: "auto" }}
              onClick={() => onAddNote(practiceId)}
              aria-label="Add note"
            >
              <FaPlus />
            </button>
          )}
        </div>
        <p className="help" style={{ margin: 0 }}>
          Add a note to log updates and post them to Slack.
        </p>
      </div>
    );
  }

  function renderResponseFields() {
    if (!responseFieldsVisible) return null;
    return (
      <div className="stack" style={{ gap: 8 }}>
        <div className="practiceSectionLabel"><span>Response details</span></div>
        {responseFields.map((field) => {
          const fieldType = String(field.type || "text").toLowerCase();
          const fieldValue = responseInputs[field.id] ?? "";
          return (
            <label key={field.id} className="stack" style={{ gap: 4 }}>
              <span className="help" style={{ marginBottom: 0, textAlign: "left", width: "100%" }}>{field.label}</span>
              {fieldType === "picklist" ? (
                <select
                  className="input"
                  value={fieldValue}
                  onChange={(e) => setResponseInputs((prev) => ({ ...prev, [field.id]: e.target.value }))}
                >
                  <option value="">Select...</option>
                  {(field.options || []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  type="text"
                  value={fieldValue}
                  onChange={(e) => setResponseInputs((prev) => ({ ...prev, [field.id]: e.target.value }))}
                />
              )}
            </label>
          );
        })}
        <p className="help" style={{ margin: 0 }} />
      </div>
    );
  }

  function renderWorkoutSection() {
    if (!practiceId) return null;
    const hasWorkouts = workouts.length > 0;
    return (
      <div className="practiceSubsection">
        <div className="practiceSectionLabel">
          <span>Workouts</span>
          {canCreateWorkouts && (
            <button
              type="button"
              className="btn ghost"
              style={{ padding: "4px 8px", minWidth: "auto" }}
              onClick={() => navigateCoachTab("workouts", { practiceId, action: "create" })}
              aria-label="Create workout"
            >
              <FaPlus />
            </button>
          )}
        </div>
        {hasWorkouts ? (
          <div className="stack" style={{ gap: 8 }}>
            {workouts.map((workout) => (
              <button
                key={workout.id || workout.name}
                className="btn ghost tiny"
                type="button"
                onClick={() =>
                  navigateCoachTab("workouts", {
                    practiceId,
                    workoutId: workout.id,
                    action: "viewWorkout",
                  })
                }
              >
                {workout.name || "Practice workout"}
              </button>
            ))}
          </div>
        ) : (
          <p className="help" style={{ margin: 0 }}>No workouts linked to this practice yet.</p>
        )}
      </div>
    );
  }

  function renderLineupSection() {
    if (!practiceId) return null;
    const hasLineups = lineupStatuses.length > 0;
    const practiceDate = resolvedPractice.startDate || resolvedPractice.date || resolvedPractice.practiceDate || practice.date;
    return (
      <div className="practiceSubsection">
        <div className="practiceSectionLabel">
          <span>Lineups</span>
          {canViewLineups && practiceDate && (
            <button
              type="button"
              className="btn ghost"
              style={{ padding: "4px 8px", minWidth: "auto" }}
              onClick={() =>
                navigateCoachTab("lineups", {
                  date: practiceDate,
                  action: "create",
                })
              }
              aria-label="Create lineup"
            >
              <FaPlus />
            </button>
          )}
        </div>
        {hasLineups ? (
          <div className="stack" style={{ gap: 8 }}>
            {lineupStatuses.map((status, idx) => (
              <div
                key={`${status.team || "team"}-${idx}`}
                className="row"
                style={{ justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{status.team || "Team"}</div>
                  {status.published && status.publishedAt ? (
                    <div className="help" style={{ marginTop: 2 }}>
                      Published {new Date(status.publishedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      {status.publishedBy ? ` • by ${status.publishedBy}` : ""}
                    </div>
                  ) : (
                    <div className="help" style={{ marginTop: 2 }}>Draft</div>
                  )}
                </div>
                {canViewLineups && practiceDate && (
                  <button
                    type="button"
                    className="btn ghost tiny"
                    onClick={() =>
                      navigateCoachTab("lineups", {
                        date: practiceDate,
                        team: status.team || null,
                      })
                    }
                  >
                    View lineup
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="help" style={{ margin: 0 }}>No lineups yet.</p>
        )}
      </div>
    );
  }

  function renderFooter() {
    if (allowEdit && isEditing && (onSave || onDelete || onCancelPractice)) {
      return (
        <div className="practiceModalFooter">
          {onCancelPractice && practiceId && !resolvedPractice.cancelled && (
            <button className="btn danger small" onClick={() => onCancelPractice(practiceId)}>
              Cancel Practice
            </button>
          )}
          {onDelete && (
            <button className="btn danger small" onClick={onDelete}>
              Delete
            </button>
          )}
          {onSave && (
            <button className="btn practiceSaveBtn" onClick={onSave}>
              Save practice
            </button>
          )}
        </div>
      );
    }

    return null;
  }

  function commitRsvp(newStatus, cleanedResponses) {
    console.log('[PracticeModal] Button clicked - newStatus:', newStatus);

    flushSync(() => {
      setOptimisticRsvpStatus(newStatus);
      setSavingRsvp(true);
    });

    // AGGRESSIVE iOS Safari repaint hack
    if (typeof window !== "undefined") {
      void document.body.offsetHeight;
      document.body.classList.add('force-repaint');
      requestAnimationFrame(() => {
        document.body.classList.remove('force-repaint');
      });
    }

    onRsvp(newStatus, cleanedResponses).then(() => {
      setSavingRsvp(false);
    }).catch(error => {
      console.error("Error saving RSVP:", error);
      setSavingRsvp(false);
      setOptimisticRsvpStatus(null);
    });
  }

  function renderRsvpButtons() {
    if (!onRsvp) return null;
    if (resolvedPractice.cancelled) return null;
    // Use optimistic status if available, otherwise use actual status
    const displayStatus = optimisticRsvpStatus !== null ? optimisticRsvpStatus : rsvpStatus;
    const normalizedStatus = typeof displayStatus === "string" ? displayStatus.toLowerCase() : null;
    const isAvailable = normalizedStatus === "available";
    const isNotAvailable = normalizedStatus === "not available";

    console.log('[PracticeModal] Rendering RSVP buttons - rsvpStatus:', rsvpStatus, 'optimistic:', optimisticRsvpStatus, 'display:', displayStatus, 'isAvailable:', isAvailable, 'isNotAvailable:', isNotAvailable);
    console.log('[PracticeModal] Active classes - Available:', isAvailable ? 'ACTIVE' : 'inactive', 'NotAvailable:', isNotAvailable ? 'ACTIVE' : 'inactive');

    // Helper function to handle click with displayStatus
    const handleButtonClick = (targetStatus) => {
      if (savingRsvp || !onRsvp || !practice?.id) {
        console.log('[PracticeModal] Click blocked - savingRsvp:', savingRsvp);
        return;
      }

      // Use displayStatus for comparison, not rsvpStatus
      const normalizedCurrent = typeof displayStatus === "string" ? displayStatus.toLowerCase() : null;
      const normalizedTarget = typeof targetStatus === "string" ? targetStatus.toLowerCase() : null;
      const newStatus = normalizedCurrent === normalizedTarget ? null : targetStatus;

      const responsesToSend = responseFieldsEnabled && newStatus
        ? responseFields.reduce((acc, field) => {
            const value = responseInputs[field.id];
            if (value == null || value === "") return acc;
            acc[field.id] = value;
            return acc;
          }, {})
        : null;
      const cleanedResponses =
        responsesToSend && Object.keys(responsesToSend).length ? responsesToSend : null;

      // Show alert when changing an existing RSVP on the same day as the practice
      const practiceDate = practice?.date?.slice(0, 10);
      const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in local time
      const isChangingExisting = normalizedCurrent !== null;
      if (practiceDate === today && isChangingExisting) {
        pendingRsvpRef.current = { newStatus, cleanedResponses };
        setShowSameDayAlert(true);
        return;
      }

      commitRsvp(newStatus, cleanedResponses);
    };

    return (
      <div key={`rsvp-${displayStatus || 'none'}`} className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <button
          key={`avail-${practiceId}-${displayStatus || 'none'}`}
          ref={(el) => {
            if (el && isAvailable) {
              // Force iOS Safari to repaint by toggling a style
              el.style.opacity = '0.99';
              requestAnimationFrame(() => {
                el.style.opacity = '';
              });
            }
          }}
          className={`availability-btn availability-btn-available ${
            isAvailable ? "active" : ""
          }`}
          data-active={isAvailable ? "true" : "false"}
          aria-pressed={isAvailable}
          style={isAvailable ? { background: "#16a34a", color: "white", borderColor: "#16a34a" } : undefined}
          onClick={() => handleButtonClick("Available")}
          disabled={savingRsvp}
          aria-label={
            isAvailable
              ? "Clear availability"
              : "Mark as available"
          }
        >
          ✓ Available
        </button>
        <button
          key={`unavail-${practiceId}-${displayStatus || 'none'}`}
          ref={(el) => {
            if (el && isNotAvailable) {
              // Force iOS Safari to repaint by toggling a style
              el.style.opacity = '0.99';
              requestAnimationFrame(() => {
                el.style.opacity = '';
              });
            }
          }}
          className={`availability-btn availability-btn-unavailable ${
            isNotAvailable ? "active" : ""
          }`}
          data-active={isNotAvailable ? "true" : "false"}
          aria-pressed={isNotAvailable}
          style={isNotAvailable ? { background: "#dc2626", color: "white", borderColor: "#dc2626" } : undefined}
          onClick={() => handleButtonClick("Not Available")}
          disabled={savingRsvp}
          aria-label={
            isNotAvailable
              ? "Clear availability"
              : "Mark as unavailable"
          }
        >
          ✗ Not Available
        </button>
      </div>
    );
  }

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalCard roster-modal practiceModalSleek" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" aria-label="Close practice modal" onClick={onClose}>
          &times;
        </button>
        <div className="practiceModalHeader">
          <div className="practiceModalEyebrow">
            {isEditing ? (practice?.id ? "Edit" : "New") : formatPracticeTypeLabel(resolvedPractice.type)}
          </div>
          <h3 className="practiceModalTitle">{displayTitle}</h3>
        </div>
        <div className="stack roster-modal-body">
          {isEditing ? renderEditForm() : renderSummary()}
          {renderDetailsSection()}
          {renderFooter()}
          {allowEdit && !hideEditToggle && !isEditing && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                className="btn"
                style={{ display: "flex", alignItems: "center", gap: 6 }}
                onClick={() => {
                  populateFormFromPractice();
                  setIsEditing(true);
                }}
              >
                <FaEdit /> Edit
              </button>
            </div>
          )}
        </div>
      </div>
      {showSameDayAlert && (
        <div className="modalOverlay" style={{ zIndex: 1100 }} onClick={(e) => e.stopPropagation()}>
          <div className="modalCard" style={{ maxWidth: 360, padding: "24px 20px", textAlign: "center" }}>
            <p style={{ fontSize: "1rem", lineHeight: 1.5, marginBottom: 20 }}>
              When you change your attendance response for a practice on the same day, it is your responsibility to reach out to your coach on Slack to alert them.
            </p>
            <button
              className="btn btn-primary"
              style={{ width: "100%" }}
              onClick={() => {
                setShowSameDayAlert(false);
                const { newStatus, cleanedResponses } = pendingRsvpRef.current || {};
                pendingRsvpRef.current = null;
                if (newStatus !== undefined) {
                  commitRsvp(newStatus, cleanedResponses);
                }
              }}
            >
              I understand
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
